import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Loader2, Zap } from 'lucide-react';
import { AxiosError } from 'axios';
import api from '@/lib/api';
import { useAuthStore, type AuthUser } from '@/stores/authStore';

const loginSchema = z.object({
  email: z.string().email('Preencha este campo para continuar.'),
  password: z.string().min(1, 'Preencha este campo para continuar.'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Atalho de desenvolvimento: preenche as credenciais e usa o MESMO POST
 * /api/login de sempre. Não existe rota que dispense senha — o backend não
 * sabe que este botão existe, e `import.meta.env.DEV` o remove do build de
 * produção.
 */
// O ternário sobre `import.meta.env.DEV` é proposital: no build de produção a
// condição vira `false` literal e o minificador apaga o objeto inteiro — as
// credenciais não chegam nem a existir no arquivo publicado.
const ATALHO_DEV = import.meta.env.DEV
  ? {
      email: (import.meta.env.VITE_DEV_EMAIL as string) || 'marcelo@condusvale.com.br',
      senha: (import.meta.env.VITE_DEV_SENHA as string) || 'condusvale',
    }
  : null;

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [entrandoDireto, setEntrandoDireto] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const autenticar = async (data: LoginFormData) => {
    setGenericError(null);
    try {
      const response = await api.post('/login', data);
      const { token, user } = response.data as { token: string; user: AuthUser };
      setAuth(token, user);
      navigate(user.trocar_senha ? '/trocar-senha' : '/');
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const message = err.response.data?.errors?.email?.[0] ?? 'E-mail ou senha incorretos.';
        setError('email', { message });
        return;
      }
      setGenericError('Não foi possível entrar. Verifique sua conexão e tente novamente.');
    }
  };

  const entrarDireto = async () => {
    setEntrandoDireto(true);
    await autenticar({ email: ATALHO_DEV!.email, password: ATALHO_DEV!.senha });
    setEntrandoDireto(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dominant px-md">
      <div className="w-full max-w-[384px] bg-surface border border-border rounded-xl shadow-sm p-lg">
        <h1 className="text-[20px] font-bold text-ink mb-lg">Entrar</h1>

        <form onSubmit={handleSubmit(autenticar)} noValidate>
          <div className="mb-md">
            <label htmlFor="email" className="block text-[12px] font-bold text-ink-soft mb-xs">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              className="w-full border border-border rounded-md px-sm py-sm text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-xs text-[14px] text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="mb-lg">
            <label htmlFor="password" className="block text-[12px] font-bold text-ink-soft mb-xs">
              Senha
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="w-full border border-border rounded-md px-sm py-sm text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
              {...register('password')}
            />
            {errors.password && (
              <p className="mt-xs text-[14px] text-destructive">{errors.password.message}</p>
            )}
          </div>

          {genericError && <p className="mb-md text-[14px] text-destructive">{genericError}</p>}

          <button
            type="submit"
            disabled={isSubmitting || entrandoDireto}
            className="w-full inline-flex items-center justify-center gap-xs bg-accent text-white font-bold text-[14px] rounded-md py-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : 'Entrar'}
          </button>
        </form>

        {import.meta.env.DEV && (
          <div className="mt-lg border-t border-dashed border-border pt-md">
            <p className="text-[11px] uppercase tracking-wide text-muted mb-sm">
              Desenvolvimento
            </p>

            <button
              type="button"
              onClick={entrarDireto}
              disabled={isSubmitting || entrandoDireto}
              className="w-full inline-flex items-center justify-center gap-xs border border-border bg-surface text-ink font-bold text-[13px] rounded-md py-sm hover:border-accent disabled:opacity-60"
            >
              {entrandoDireto ? (
                <Loader2 className="animate-spin" size={15} />
              ) : (
                <>
                  <Zap size={15} />
                  Entrar direto como {ATALHO_DEV?.email.split('@')[0]}
                </>
              )}
            </button>

            <p className="mt-xs text-[11px] text-muted leading-snug">
              Só aparece rodando em desenvolvimento. Usa o mesmo login de sempre — no build de
              produção este bloco não existe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
